import axios from 'axios';

// Create axios instance with timeout
const api = axios.create({
  baseURL: '/api', // Adjust API base if needed
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second default timeout
});

// Add request interceptor to add auth token and log slow requests
api.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: new Date() };

    // Add Authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to log slow responses
api.interceptors.response.use(
  (response) => {
    if (response.config.metadata) {
      const endTime = new Date();
      const duration = endTime - response.config.metadata.startTime;
      if (duration > 5000) {
        console.warn(`Slow API call to ${response.config.url}: ${duration}ms`);
      }
    }
    return response;
  },
  (error) => {
    if (error.config?.metadata) {
      const endTime = new Date();
      const duration = endTime - error.config.metadata.startTime;
      console.error(`Failed API call to ${error.config.url}: ${duration}ms`, error.message);
    }
    return Promise.reject(error);
  }
);

export const loginUser = async (email, password, token = null) => {
  if (token) {
    // Use the token to fetch user details
    return api.post('/login', { token }).then((res) => res.data);
  } else {
    // Normal login flow using email and password
    return api.post('/login', { email, password }).then((res) => res.data);
  }
};

export const getDfcDraftRankings = async (wp_id, source) => {
  return api.post('/getDfcDraftRankings', { wp_id, source }).then((res) => res.data);
};

export const editNiceUsername = async (wp_id, username) => {
  return api.post('/editNiceUsername', { wp_id, username }).then((res) => res.data);
};

export const submitDraftRankings = async (wp_id, source, rankings) => {
  return api.post('/submitDraftRankings', { wp_id, source, rankings }).then((res) => res.data);
};

export const getLineup = async (gw) => {
  return api.post('/getLineups', { gw }).then((res) => res.data);
};

export const setLineup = async (selectedPlayers, gw, lineupCommentary = []) => {
  return api.post('/setLineups', { selectedPlayers, gw, lineupCommentary }).then((res) => res.data);
};

export const getPrevLineup = async (gw) => {
  return api.post('/getPreviousLineups', { gw }).then((res) => res.data);
};

export const getPlayerStatsLabels = async () =>
  api.post('/getPlayerStatsLabels', {}).then((res) => res.data);

// Fetch team-specific stat labels
export const getTeamStatsLabels = async () =>
  api.post('/getTeamStatsLabels', {}).then((res) => res.data);

// Fetch modelling features (stats with use_in_modelling = 1)
export const getModellingFeatures = async () =>
  api.post('/getModellingFeatures', {}).then((res) => res.data);

export const setPlayerStatsLabels = (payload) =>
  api.post('/setPlayerStatsLabels', payload).then((res) => res.data);

export const setTeamStatsLabels = (payload) =>
  api.post('/setTeamStatsLabels', payload).then((res) => res.data);

// Availability stats labels
export const getAvailabilityStatsLabels = async () =>
  api.post('/getAvailabilityStatsLabels', {}).then((res) => res.data);

export const setAvailabilityStatsLabels = (payload) =>
  api.post('/setAvailabilityStatsLabels', payload).then((res) => res.data);

export const getModellingOutputs = async (user, activeLeague, gw_status) =>
  api.post('/getModellingOutputs', { user, activeLeague, gw_status }).then((res) => res.data);

// Model Outputs - Focused endpoints per tab
export const getTeamPredictions = (club_optaID = null) =>
  api.post('/getTeamPredictions', { club_optaID }).then((res) => res.data);

// Season × gameweek grids (Team Results / Player Results tabs)
export const getTeamResultsGrid = (compSeasonID = null) =>
  api.post('/getTeamResultsGrid', { compSeasonID }).then((res) => res.data);

export const getPlayerResultsGrid = (compSeasonID = null) =>
  api.post('/getPlayerResultsGrid', { compSeasonID }).then((res) => res.data);

export const getPlayerAccuracySummary = (club_optaID = null) =>
  api.post('/getPlayerAccuracySummary', { club_optaID }).then((res) => res.data);

// Prediction Explanation (SHAP values)
export const getPredictionExplanation = (model_type, entity_id, fixture_id) =>
  api.post('/getPredictionExplanation', { model_type, entity_id, fixture_id }).then((res) => res.data);

export const logoutUser = (token) => api.post('/logout', { token }).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Revenue Analytics API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getRevenueTimeSeries = (filters = {}) =>
  api.post('/getRevenueTimeSeries', { granularity: 'month', ...filters }).then((res) => res.data);

// Daily this-month-vs-last-year pacing. align: 'auto' (default) = deadline-
// aligned for August (the pre-season ramp tracks the GW1 deadline), calendar
// for every other month; 'deadline' / 'calendar' force either.
export const getRevenuePacing = (filters = {}) =>
  api.post('/getRevenuePacing', { align: 'auto', ...filters }).then((res) => res.data);

// Renewals already scheduled to bill in the rest of the month (committed,
// not a forecast — it can't know about sales that haven't happened).
export const getUpcomingPayments = (filters = {}) =>
  api.post('/getUpcomingPayments', filters).then((res) => res.data);

// Month / season-end / calendar-year / next-12m forecast from the pipeline's
// subscription-book model. { horizon: 'month'|'season'|'calendar_year'|'next_12m',
// month?: 'YYYY-MM', season?: 2026 }
export const getRevenueForecast = (params = {}) =>
  api.post('/getRevenueForecast', { horizon: 'month', ...params }).then((res) => res.data);

export const getSubscriberMetrics = (filters = {}) =>
  api.post('/getSubscriberMetrics', { granularity: 'month', ...filters }).then((res) => res.data);

export const getRecentOrders = (filters = {}) =>
  api.post('/getRecentOrders', { page: 1, page_size: 25, ...filters }).then((res) => res.data);

export const getTopCustomers = (filters = {}) =>
  api.post('/getTopCustomers', { limit: 50, ...filters }).then((res) => res.data);

export const getChurnAnalysis = (filters = {}) =>
  api.post('/getChurnAnalysis', filters).then((res) => res.data);

export const getRevenueBreakdown = (filters = {}) =>
  api.post('/getRevenueBreakdown', filters).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Advanced Revenue Analytics API Functions (MRR Waterfall, Cohorts, LTV)
// ──────────────────────────────────────────────────────────────────────────────

export const getMRRWaterfall = (filters = {}) =>
  api.post('/getMRRWaterfall', filters).then((res) => res.data);

export const getCohortRetention = (filters = {}) =>
  api.post('/getCohortRetention', { max_periods: 12, ...filters }).then((res) => res.data);

export const getAdvancedMetrics = (filters = {}) =>
  api.post('/getAdvancedMetrics', filters).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Web Analytics API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getWebAnalyticsOverview = (filters = {}) =>
  api.post('/getWebAnalyticsOverview', filters).then((res) => res.data);

export const getPageMetrics = (filters = {}) =>
  api.post('/getPageMetrics', filters).then((res) => res.data);

export const getFeatureUsage = (filters = {}) =>
  api.post('/getFeatureUsage', filters).then((res) => res.data);

export const getTrafficSources = (filters = {}) =>
  api.post('/getTrafficSources', filters).then((res) => res.data);

export const getStorefrontFunnel = (filters = {}) =>
  api.post('/getStorefrontFunnel', filters).then((res) => res.data);

export const getFunnelBySource = (filters = {}) =>
  api.post('/getFunnelBySource', filters).then((res) => res.data);

export const getUserJourney = (wpId) =>
  api.post('/getUserJourney', { wp_id: wpId }).then((res) => res.data);

export const getWebChurnAnalysis = (filters = {}) =>
  api.post('/getWebChurnAnalysis', filters).then((res) => res.data);

export const getGameweekTimeline = () =>
  api.post('/getGameweekTimeline', {}).then((res) => res.data);

export const getDataFreshness = () =>
  api.post('/getDataFreshness', {}).then((res) => res.data);

export const getFeatureUsageSummary = (filters = {}) =>
  api.post('/getFeatureUsageSummary', filters).then((res) => res.data);

export const getChurnTrend = () =>
  api.post('/getChurnTrend', {}).then((res) => res.data);

// Aug 2026 overhaul — Product Usage / Acquisition / Retention
export const getFeatureAdoption = (filters = {}) =>
  api.post('/getFeatureAdoption', filters).then((res) => res.data);

export const getPowerUserContrast = (filters = {}) =>
  api.post('/getPowerUserContrast', filters).then((res) => res.data);

export const getNewSubscriberDiscovery = (days = 60) =>
  api.post('/getNewSubscriberDiscovery', { days }).then((res) => res.data);

export const getGameweekRhythm = () =>
  api.post('/getGameweekRhythm', {}).then((res) => res.data);

export const getConversionPaths = (filters = {}) =>
  api.post('/getConversionPaths', filters).then((res) => res.data);

export const getAcquisitionFunnel = (weeks = 8) =>
  api.post('/getAcquisitionFunnel', { weeks }).then((res) => res.data);

export const getDormantSubscribers = () =>
  api.post('/getDormantSubscribers', {}).then((res) => res.data);

export const getCancelFlowFunnel = (filters = {}) =>
  api.post('/getCancelFlowFunnel', filters).then((res) => res.data);

export const getChurnContext = (filters = {}) =>
  api.post('/getChurnContext', filters).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// User Analytics API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getUserAnalytics = (filters = {}) =>
  api.post('/getUserAnalytics', { min_league_size: 3, ...filters }).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Discord API Functions
// ──────────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────────
// Blog API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getAdminBlogPosts = (filters = {}) =>
  api.post('/admin/getBlogPosts', filters).then((res) => res.data);

export const getAdminBlogPost = (id) =>
  api.post('/admin/getBlogPost', { id }).then((res) => res.data);

export const createBlogPost = (data) =>
  api.post('/admin/createBlogPost', data).then((res) => res.data);

export const updateBlogPost = (data) =>
  api.post('/admin/updateBlogPost', data).then((res) => res.data);

export const deleteBlogPost = (id) =>
  api.post('/admin/deleteBlogPost', { id }).then((res) => res.data);

export const getAdminBlogCategories = () =>
  api.post('/admin/getBlogCategories', {}).then((res) => res.data);

export const createBlogCategory = (data) =>
  api.post('/admin/createBlogCategory', data).then((res) => res.data);

export const updateBlogCategory = (data) =>
  api.post('/admin/updateBlogCategory', data).then((res) => res.data);

export const getAutoTaxonomy = () =>
  api.post('/admin/getAutoTaxonomy', {}).then((res) => res.data);

export const uploadBlogImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/uploadBlogImage', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);
};

// ──────────────────────────────────────────────────────────────────────────────
// Branding API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getBranding = () =>
  api.post('/getBranding', {}).then((res) => res.data);

export const uploadBrandingAsset = (assetKey, file) => {
  const formData = new FormData();
  formData.append('asset_key', assetKey);
  formData.append('file', file);
  return api.post('/admin/uploadBrandingAsset', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);
};

// ──────────────────────────────────────────────────────────────────────────────
// Admin Settings API Functions
// ──────────────────────────────────────────────────────────────────────────────

// Users
export const getUsers = (params = {}) =>
  api.post('/admin/getUsers', params).then((res) => res.data);

export const setAdminStatus = (data) =>
  api.post('/admin/setAdminStatus', data).then((res) => res.data);

export const setContentCreatorStatus = (data) =>
  api.post('/admin/setContentCreatorStatus', data).then((res) => res.data);

export const checkAdmin = (wp_id) =>
  api.post('/admin/checkAdmin', { wp_id }).then((res) => res.data);

// App Settings
export const getAppSettings = () =>
  api.post('/admin/getAppSettings', {}).then((res) => res.data);

export const setAppSetting = (setting_key, setting_value) =>
  api.post('/admin/setAppSetting', { setting_key, setting_value }).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Creators API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const getCreators = () =>
  api.post('/admin/getCreators', {}).then((res) => res.data);

export const getCreatorReferrals = (wp_id) =>
  api.post('/admin/getCreatorReferrals', { wp_id }).then((res) => res.data);

export const getCreatorEnquiries = () =>
  api.post('/admin/getCreatorEnquiries', {}).then((res) => res.data);

export const setCreatorEnquiryStatus = (data) =>
  api.post('/admin/setCreatorEnquiryStatus', data).then((res) => res.data);

// ──────────────────────────────────────────────────────────────────────────────
// Social Share API Functions
// ──────────────────────────────────────────────────────────────────────────────

export const verifyDiscord = () =>
  api.get('/verifyDiscord').then((res) => res.data);

export const postDiscord = (data) =>
  api.post('/postDiscord', data).then((res) => res.data);

// YouTube videos from API (fetches server-side to avoid CORS)
export const getYouTubeVideos = () =>
  api.get('/getYouTubeVideos').then((res) => res.data.videos);

// ──────────────────────────────────────────────────────────────────────────────
// Blog Generator (post-centric, async runs) — /blogGen/*
// All responses are { data } unless noted.
// ──────────────────────────────────────────────────────────────────────────────

const bg = (path, body = {}) => api.post(`/blogGen/${path}`, body).then((res) => res.data);

export const blogGenVideos = () => bg('videos').then((r) => r.data);
// → { post_id, run_id, reused, conflict, posts }
export const blogGenStart = (data) => bg('start', data);
export const blogGenState = (post_id) => bg('state', { post_id }).then((r) => r.data);
export const blogGenRun = (run_id, with_prompts = false) =>
  bg('run', { run_id, with_prompts }).then((r) => r.data);
export const blogGenUpdatePlayers = (post_id, rows) =>
  bg('players/update', { post_id, rows }).then((r) => r.data.player_cards);
export const blogGenAddPlayer = (post_id, element, role = 'featured', raw_name = null) =>
  bg('players/add', { post_id, element, role, raw_name }).then((r) => r.data.player_cards);
export const blogGenDismissUnresolved = (post_id, raw_name) => bg('players/dismiss', { post_id, raw_name });
export const blogGenRefreshStats = (post_id) =>
  bg('players/refresh', { post_id }).then((r) => r.data.player_cards);
export const blogGenSearchPlayers = (q, team = null, limit = 25) =>
  bg('players/search', { q, team, limit }).then((r) => r.data.results);
export const blogGenUpdateOutline = (data) => bg('outline/update', data).then((r) => r.data);
export const blogGenPreview = (post_id, kind = 'write_blog', template_override = null) =>
  bg('preview', { post_id, kind, template_override }).then((r) => r.data);
// → { run_id, reused }
export const blogGenWrite = (data) => bg('write', data);
export const blogGenSection = (data) => bg('section', data);
export const blogGenVerify = (post_id) => bg('verify', { post_id });
export const blogGenRetable = (post_id) => bg('retable', { post_id }).then((r) => r.data);
export const blogGenGetVersion = (post_id, version_id) =>
  bg('versions/get', { post_id, version_id }).then((r) => r.data);
export const blogGenRestoreVersion = (post_id, version_id) =>
  bg('versions/restore', { post_id, version_id }).then((r) => r.data);
export const blogGenSetStatus = (post_id, status) => bg('status', { post_id, status }).then((r) => r.data);
export const blogGenPreviewLink = (post_id) => bg('previewLink', { post_id }).then((r) => r.data);
export const blogGenRunsRecent = () => bg('runs/recent').then((r) => r.data.runs);
export const blogGenRunsStats = () => bg('runs/stats').then((r) => r.data);

// ──────────────────────────────────────────────────────────────────────────────
// Blog prompt templates + blog types — /admin/blogPrompts/*  (all { data })
// ──────────────────────────────────────────────────────────────────────────────

const bp = (path, body = {}) => api.post(`/admin/blogPrompts/${path}`, body).then((res) => res.data.data);

// → { templates, defaults, variables, blog_types, models }
export const blogPromptsList = (kind = null) => bp('list', kind ? { kind } : {});
export const blogPromptsGet = (id) => bp('get', { id });
export const blogPromptsLint = (kind, content, post_id = null) => bp('lint', { kind, content, post_id });
export const blogPromptsUpdate = (data) => bp('update', data);
export const blogPromptsAdoptDefault = (id) => bp('adoptDefault', { id });
export const blogPromptsCreateOverride = (kind, blog_type_id) => bp('createOverride', { kind, blog_type_id });
export const blogPromptsDelete = (id) => bp('delete', { id });
export const blogPromptsVersions = (id) => bp('versions', { id }).then((d) => d.versions);
export const blogPromptsRestore = (id, version_id) => bp('restore', { id, version_id });
export const blogPromptsDiff = (id, against = 'default') => bp('diff', { id, against });
export const blogTypesUpdate = (data) => bp('blogTypes/update', data);

export default api;
